import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { RootState } from '../../../store/store';
import ContextUnavailable from './ContextUnavailable';

/** Where the child's space lives. */
export const KID_HOME = '/student';

/**
 * Where a parent's URL lands inside the child's space.
 *
 * A course or teacher link is the same public object on both sides, so it opens
 * the Student's reading of it (DEC-S2-S03-01, S05-01), with the catalog's
 * filters kept. Everything else is a parent's own screen and goes home.
 */
export function kidPathFor(pathname: string, search = ''): string {
  if (pathname === '/courses') return `/student/explore${search}`;
  const course = pathname.match(/^\/courses\/([^/]+)$/);
  if (course) return `/student/explore/${course[1]}`;
  const teacher = pathname.match(/^\/teachers\/([^/]+)$/);
  if (teacher) return `/student/teachers/${teacher[1]}`;
  return KID_HOME;
}

/**
 * GAP-S01-01 — keeps a parent's own screens away from a session in kid mode.
 *
 * Kid mode is the parent's session, so every parent route is one URL away
 * from the child. This sends any of them into the child's space while the
 * server says kid mode is open, and — for a parent — waits for the server to
 * say so before rendering anything, so a refresh does not flash the console
 * at a child for a frame. The server refuses the sensitive actions anyway;
 * this is what keeps the screens that offer them out of sight.
 */
const KidModeGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const kidMode = useSelector((state: RootState) => state.context.kidMode);
  const restored = useSelector((state: RootState) => state.context.restored);
  const restoreFailed = useSelector((state: RootState) => state.context.restoreFailed);
  const location = useLocation();

  if (!isAuthenticated || role !== 'PARENT') return <>{children}</>;
  if (restoreFailed) return <ContextUnavailable />;
  if (!restored) return null;
  if (kidMode) return <Navigate to={kidPathFor(location.pathname, location.search)} replace />;
  return <>{children}</>;
};

export default KidModeGate;
